import { useQuery } from '@tanstack/react-query';
import { invalidateAll } from '../../api/queryClient';
import { trpc, type RouterOutputs } from '../../api/trpc';

export type ProjectListItem = RouterOutputs['projects']['list'][number];
export type ProjectDetail = RouterOutputs['projects']['get'];
export type ProjectInfo = ProjectDetail['project'];
export type ProjectUnit = ProjectDetail['units'][number];
export type ProjectPhoto = ProjectDetail['photos'][number];
export type ProjectContact = ProjectDetail['contacts'][number];
export type GeocodeResult = RouterOutputs['projects']['geocode'][number];
export type PdSyncOutcome = RouterOutputs['projects']['contacts']['syncToPipedrive'];

export function useProjects() {
  return useQuery(trpc.projects.list.queryOptions());
}

export function useProject(id: string) {
  return useQuery(trpc.projects.get.queryOptions({ id }, { enabled: id !== '' }));
}

/** Drop every cached project query (list + details) after a write. */
export function invalidateProjects(): Promise<void> {
  return invalidateAll(trpc.projects.pathFilter());
}

/** Stored project file (photo/brochure) — served by the API, auth cookie applies. */
export function projectFileUrl(fileId: string): string {
  return `/api/projects/files/${fileId}`;
}

// --- Website monitoring (scrape module) ---

export type SiteStatus = RouterOutputs['scrape']['status'];
export type SiteChange = SiteStatus['pendingChanges'][number];
export type SiteCheckRun = SiteStatus['runs'][number];
export type ScanResult = RouterOutputs['scrape']['scan'];
export type SiteImportSummary = RouterOutputs['scrape']['importSiteData'];

const runActive = (status: string): boolean => status === 'queued' || status === 'running';

/** Website sync status, polled every 3 s while a check is queued or running. */
export function useProjectSite(projectId: string) {
  return useQuery(
    trpc.scrape.status.queryOptions(
      { projectId },
      {
        enabled: projectId !== '',
        refetchInterval: (query) => (query.state.data?.runs.some((r) => runActive(r.status)) ? 3000 : false),
      },
    ),
  );
}

export function invalidateSite(): Promise<void> {
  return invalidateAll(trpc.scrape.pathFilter());
}
